import { sortRegionsByPathDepth } from './sort-regions-by-path-depth';
import { normalizeCity } from './format-listing-location';

type RegionLike = {
  name?: string | null;
  path?: string | null;
  placeId?: string | null;
};

export interface ListingBreadcrumbItem {
  /** Stable key for React rendering. */
  key: string;
  /** Short display name (first comma-segment of the region name). */
  name: string;
  /** Link target; absent for non-linkable crumbs (no region page exists). */
  href?: string;
}

/** Counts non-empty path segments: "/united-states/florida" -> 2. */
function countPathSegments(path: string | null | undefined): number {
  if (!path) return 0;
  return path.split('/').filter(Boolean).length;
}

/**
 * A city-level region path has at least 3 non-empty segments
 * (country/state/city). State-level has 2, country-level 1.
 */
export function hasCityLevelRegion(
  regions: ReadonlyArray<RegionLike>
): boolean {
  return regions.some((region) => countPathSegments(region.path) >= 3);
}

/**
 * Builds the visible breadcrumb items for the listing detail page,
 * deepest-first (city -> state -> country).
 *
 * Most listings' `listings_regions` rows stop at state level (BHT-6258:
 * ~69% of Inlet Beach listings have no city-level region), which used to
 * render a breadcrumb of just "Florida" while the search card showed
 * "Inlet Beach, FL". When no city-level region exists, we prepend a
 * non-linked crumb from the listing's own city field so the detail page
 * matches the card.
 */
export function buildListingBreadcrumbs(
  regions: ReadonlyArray<RegionLike | null | undefined> | null | undefined,
  city: string | null | undefined
): ListingBreadcrumbItem[] {
  const presentRegions = (regions ?? []).filter(
    (r): r is RegionLike => r !== null && r !== undefined
  );

  const deepestFirst = sortRegionsByPathDepth([...presentRegions]).reverse();

  const items: ListingBreadcrumbItem[] = deepestFirst.map((region, index) => ({
    key: region.placeId ?? region.path ?? `region-${index}`,
    name: (region.name ?? '').split(',')[0].trim(),
    ...(region.path ? { href: `/vacation-rentals${region.path}` } : {}),
  }));

  if (!hasCityLevelRegion(presentRegions)) {
    const fallbackCity = normalizeCity(city);
    const duplicatesDeepest =
      fallbackCity !== undefined &&
      items[0] !== undefined &&
      items[0].name.toLowerCase() === fallbackCity.toLowerCase();

    if (fallbackCity !== undefined && !duplicatesDeepest) {
      items.unshift({ key: `city-${fallbackCity}`, name: fallbackCity });
    }
  }

  return items;
}
